import type { HelpTopic, HelpTopicId } from './topics';

/**
 * German in-app help (Swiss spelling, no ß; "Sie"). Same topics and order as the English texts in topics.ts; the
 * labels in quotes match the German UI.
 */
export const helpTopicsDe: Record<HelpTopicId, HelpTopic> = {
  tenants: {
    title: 'Mandanten',
    intro:
      'Ein Mandant ist eine Agentur (ein Kunde), die jd agentic nutzt. Alles ist pro Mandant getrennt: Agenten, Konversationen, Kontakte, Einstellungen und Benutzer. Kein Mandant sieht die Daten eines anderen.',
    sections: [
      {
        heading: 'Was Sie hier sehen',
        items: [
          'Name und Slug: der Name des Mandanten und seine kurze, feste Kennung (z. B. muster-ag).',
          'Zeitzone und Sprachen: wann Agenten Personen schreiben dürfen und in welchen Sprachen.',
          'jd enterprise: «Geprüft» heisst, jd agentic erreicht die Daten dieses Mandanten in jd enterprise. «Nicht geprüft» heisst, die Verbindung ist noch nicht eingerichtet oder funktioniert nicht.',
          'Status: Aktive Mandanten laufen normal. Gesperrte Mandanten sind pausiert. Archivierte werden nur noch aufbewahrt.',
        ],
      },
      {
        heading: 'Mandant hinzufügen',
        body: [
          'Klicken Sie auf «Neuer Mandant» und geben Sie Name, Slug, Zeitzone und Sprachen ein. Öffnen Sie danach den Mandanten, um die Einrichtung abzuschliessen.',
        ],
      },
      {
        heading: 'Checkliste für einen neuen Mandanten',
        numbered: true,
        items: [
          'jd enterprise: das API-Konto des Mandanten verbinden, damit Agenten Daten lesen und speichern können.',
          'Kanäle: WhatsApp, SMS oder E-Mail verbinden, damit Agenten mit Personen schreiben können.',
          'Benutzer: die Personen erfassen, die prüfen und freigeben, was Agenten sammeln.',
          'Einstellungen: Ruhezeiten festlegen und wer benachrichtigt wird.',
        ],
      },
      {
        heading: 'Wer diese Seite sieht',
        body: [
          'Nur Plattform-Admins sehen die Liste aller Mandanten und können neue anlegen. Ein Mandanten-Admin sieht nur den eigenen Mandanten, unter «Organisation».',
        ],
      },
    ],
  },

  tenant: {
    title: 'Mandanten-Einstellungen',
    intro:
      'Alles, was ein Mandant zum Arbeiten braucht: die Verbindung zu jd enterprise, die Kanäle, über die Agenten mit Personen schreiben, die Benutzer und die Regeln. Die Reiter gehen von der Grundeinrichtung (links) bis zur Feinabstimmung (rechts).',
    sections: [
      {
        heading: 'Allgemein (Plattform-Admins)',
        body: [
          'Name, Zeitzone und Sprachen. «Modell-Datenresidenz» legt fest, wo die KI-Modelle laufen dürfen: überall oder nur in der EU und der Schweiz.',
        ],
      },
      {
        heading: 'jd enterprise',
        body: [
          'Das eigene API-Konto des Mandanten für jd enterprise. Agenten lesen und speichern Daten nur über dieses Konto und nur im Rahmen seiner Berechtigungen (Scopes). «Prüfen» testet, ob jd enterprise das Konto akzeptiert.',
        ],
      },
      {
        heading: 'Kanäle',
        body: [
          'Wie Agenten Personen erreichen: WhatsApp (der Hauptkanal), SMS (als Ausweichkanal) und E-Mail. Jeder Kanal braucht eigene Zugangsdaten. «Prüfen» testet sie mit einer Anfrage.',
        ],
      },
      {
        heading: 'WhatsApp-Einwilligung',
        body: [
          'Wer WhatsApp-Nachrichten zugestimmt oder sie abgelehnt hat. Wer abgelehnt hat (z. B. mit der Antwort STOP), erhält nie wieder WhatsApp-Nachrichten.',
        ],
      },
      {
        heading: 'Benutzer',
        body: [
          'Die Personen, die sich für diesen Mandanten anmelden können, und ihre Rollen. Prüfer geben frei, was Agenten gesammelt haben; Admins können Einstellungen ändern. Bitten Sie alle Benutzer, auf ihrer Profilseite die Zwei-Faktor-Anmeldung einzuschalten.',
        ],
      },
      {
        heading: 'Berechtigungen und Modell-Policy (Plattform-Admins)',
        body: [
          'Berechtigungen sind Schalter, die festlegen, was der Mandant selbst ändern darf, z. B. eigene Agenten bauen oder den KI-Designer nutzen. Die Modell-Policy legt fest, welches KI-Modell für welche Aufgabe verwendet wird; leer heisst Plattform-Standard.',
        ],
      },
      {
        heading: 'Einstellungen',
        body: [
          'Ruhezeiten (nachts keine neuen Nachrichten, auf Wunsch auch nicht am Wochenende), wer über Prüfungen und Eskalationen informiert wird, welche Benutzer prüfen und welche Antwort Personen erhalten, die ohne offene Konversation schreiben.',
          'Testmodus: Solange die Liste «Testmodus: nur diese Empfänger» nicht leer ist, senden Agenten Nachrichten nur an diese Nummern und E-Mail-Adressen; alle anderen werden übersprungen. Nutzen Sie dies, wenn Agenten mit Testdaten arbeiten, die Kontakte echter Personen enthalten. Solange der Testmodus aktiv ist, erscheint oben ein gelbes Abzeichen «Testmodus»; leeren Sie die Liste, um ihn auszuschalten.',
        ],
      },
    ],
  },

  rulesets: {
    title: 'Regelwerke',
    intro:
      'Ein Regelwerk ist eine Liste fester Prüfungen, die ein Agent auf gesammelte Daten anwendet, z. B. «eine AHV-Nummer hat 13 Ziffern und beginnt mit 756». Die Prüfungen sind einfache Regeln, keine KI, und liefern deshalb immer dasselbe Ergebnis.',
    sections: [
      {
        heading: 'Was Sie hier sehen',
        items: [
          'Links: alle Regelwerke. Eingebaute (mit Schloss) gehören zur Plattform, z. B. Prüfungen für AHV, IBAN oder Bewilligungen. Sie können sie ansehen und testen, aber nicht ändern.',
          'Plattform-Regelwerke gelten für alle Mandanten; Mandanten-Regelwerke nur für einen.',
          'Rechts: die Regeln des gewählten Regelwerks und ein Tester.',
        ],
      },
      {
        heading: 'Regeln',
        items: [
          '«Fehler (blockiert)»: der Wert ist falsch, und der Agent fragt die Person noch einmal.',
          '«Hinweis (Prüfung)»: der Wert wird übernommen, aber markiert, damit die prüfende Person genau hinschaut.',
          'Pfad sagt, welches Feld geprüft wird; «Wenn» ist eine optionale Bedingung; «Bedingung» ist die Regel selbst; «Meldung» wird angezeigt, wenn die Prüfung fehlschlägt.',
        ],
      },
      {
        heading: 'Regelwerk testen',
        body: [
          'Fügen Sie ein Beispiel als JSON ein und klicken Sie auf «Ausführen». Sie sehen «OK» oder die Fehler und Hinweise mit dem betroffenen Feld. Es wird nichts gespeichert oder gesendet.',
        ],
      },
      {
        heading: 'Wer Regelwerke ändern darf',
        body: [
          'Admins und Builder können die Regelwerke ihres Mandanten erstellen und ändern; Plattform-Regelwerke nur Plattform-Admins. Schlüssel von Mandanten-Regelwerken dürfen nicht mit «ch.» beginnen (für die Plattform reserviert). Jede Änderung steht im Audit-Log.',
        ],
      },
    ],
  },

  tools: {
    title: 'Tool-Registry',
    intro:
      'Tools sind die Aktionen, die Agenten in jd enterprise ausführen dürfen, z. B. «Kontakte ohne AHV-Nummer finden» oder «AHV-Nummer speichern». Fast alle stammen aus der Beschreibung der jd-enterprise-API (jde-napi); hier wird festgelegt, welche davon Agenten nutzen dürfen.',
    sections: [
      {
        heading: 'Was die Abzeichen bedeuten',
        items: [
          '«none»: das Tool liest nur Daten.',
          '«staged»: das Tool bereitet eine Änderung vor, die in jd enterprise noch jemand bestätigen muss.',
          '«authoritative» (rot): das Tool ändert echte Daten in jd enterprise direkt.',
          '«not callable»: Agenten können dieses Tool im Moment nicht verwenden.',
        ],
      },
      {
        heading: 'Wann ein Agent ein Tool nutzen kann',
        body: [
          'Nur wenn es eingeschaltet ist («Für Agents freigegeben»), kein internes Tool ist und, falls es echte Daten ändert (authoritative), ein Plattform-Admin zusätzlich «Autoritative Schreibzugriffe freigegeben» eingeschaltet hat. Auch dann blockieren eingebaute Sicherheitsregeln Tools, die etwas befördern oder genehmigen, und Agenten speichern Daten erst, nachdem eine Person sie bei den Prüfungen freigegeben hat.',
        ],
      },
      {
        heading: 'Aus OpenAPI importieren',
        body: [
          'Liest die aktuelle Beschreibung der jd-enterprise-API neu ein und zeigt, was hinzugekommen, weggefallen oder geändert ist. Ihre Schalter und Texte bleiben erhalten.',
        ],
      },
      {
        heading: 'Wer diese Seite sieht',
        body: ['Nur Plattform-Admins. Jede Änderung steht im Audit-Log.'],
      },
    ],
  },

  audit: {
    title: 'Audit-Log',
    intro:
      'Ein Protokoll aller Änderungen: Einstellungen, Agenten, Deployments, Prüfentscheide, Übernahmen und jeder Wert, den ein Agent in jd enterprise gespeichert hat. Einträge können nie geändert oder gelöscht werden.',
    sections: [
      {
        heading: 'Was Sie hier sehen',
        items: [
          'Wann: Zeitpunkt der Änderung (Ihre Ortszeit).',
          'Akteur: wer es getan hat, ein Benutzer, ein Agent, das System oder ein API-Schlüssel.',
          'Aktion und Ressource: was womit gemacht wurde (z. B. agent.delete an einem Agenten).',
          '«Anzeigen»: die Werte vor und nach der Änderung. Geheime Werte werden nie protokolliert.',
        ],
      },
      {
        heading: 'Etwas finden',
        body: [
          'Ressourcentyp, Ressourcen-ID oder Akteur-ID (exakte Werte) eingeben und auf «Filtern» klicken. Die neuesten Einträge stehen oben; «Mehr laden» zeigt ältere.',
        ],
      },
      {
        heading: 'Wer diese Seite sieht',
        body: ['Plattform-Admins und Mandanten-Admins. Ein Mandanten-Admin sieht nur die Einträge des eigenen Mandanten.'],
      },
    ],
  },

  profile: {
    title: 'Ihr Profil',
    intro: 'Ihr Konto, Ihr Passwort und die Zwei-Faktor-Anmeldung.',
    sections: [
      {
        heading: 'Passwort',
        body: [
          'Geben Sie Ihr aktuelles Passwort einmal und das neue zweimal ein (mindestens 12 Zeichen). Nach der Änderung werden Sie überall sonst abgemeldet; dieser Browser bleibt angemeldet.',
        ],
      },
      {
        heading: 'Zwei-Faktor-Anmeldung',
        body: [
          'Klicken Sie auf «2FA einrichten», scannen Sie den QR-Code mit einer Authenticator-App (Google Authenticator, Microsoft Authenticator, …) und geben Sie den 6-stelligen Code ein. Danach brauchen Sie bei jeder Anmeldung einen Code aus der App. Bitte schalten Sie sie ein.',
        ],
      },
    ],
  },

  dashboard: {
    title: 'Übersicht',
    intro:
      'Wie es den Agenten geht, jetzt gerade und in den letzten 30 Tagen. Plattform-Benutzer sehen alle Mandanten, bis sie oben einen auswählen.',
    sections: [
      {
        heading: 'Jetzt',
        items: [
          'Läufe heute und aktive Läufe: wie viele Agenten-Läufe heute gestartet sind und wie viele noch laufen.',
          'Offene Prüfungen: Einträge, die auf die Freigabe einer Person warten. Rot, wenn einer überfällig ist.',
          'Eskalationen: Fälle, die die Agenten an Ihr Team übergeben haben.',
          'Fehler heute: Läufe, die mit einem Fehler endeten. Rot ab dem ersten.',
          'Ein Klick auf eine Kachel öffnet die passende Liste.',
        ],
      },
      {
        heading: 'Letzte 30 Tage',
        items: [
          'Abschlussquote: Anteil der Läufe, die ihr Ziel erreicht haben.',
          'Eskalations- und Eingriffsquote: wie oft eine Person eingreifen musste.',
          'Modellkosten und Kosten pro abgeschlossenem Lauf, in CHF.',
          'Läufe pro Tag: mit der Maus über einen Balken fahren zeigt gestartete, abgeschlossene und fehlgeschlagene Läufe.',
        ],
      },
    ],
  },

  agents: {
    title: 'Agenten',
    intro: 'Alle Agenten des gewählten Mandanten und die Vorlagen (fertige Agenten), mit denen man starten kann.',
    sections: [
      {
        heading: 'Was die Abzeichen bedeuten',
        items: [
          '«Deployt vN»: Version N ist eingeschaltet und arbeitet. «Nicht deployt»: der Agent existiert, läuft aber nicht.',
          '«Pausiert»: eingeschaltet, aber vorläufig angehalten. «Deaktiviert»: ausgeschaltet.',
          'Ein Vorlagenname mit Version: der Agent basiert auf dieser Vorlage. «Eigen»: von Grund auf gebaut.',
          'Vorlagen: «Veröffentlicht vN» können Mandanten nutzen; «Entwurf» ist noch in Arbeit.',
        ],
      },
      {
        heading: 'Was Sie tun können',
        body: [
          'Nach Name, Kurzname oder Tag suchen. Ein Klick auf einen Agenten oder eine Vorlage zeigt die Versionen. «Neuer Agent» erstellt einen aus einer Vorlage, von Grund auf oder aus einer Beschreibung, die Sie schreiben.',
        ],
      },
    ],
  },

  newAgent: {
    title: 'Agent erstellen',
    intro: 'Drei Wege zum Start. Im Designer können Sie später alles ändern.',
    sections: [
      {
        heading: 'Aus Vorlage (am einfachsten)',
        body: [
          'Wählen Sie eine veröffentlichte Vorlage und geben Sie dem Agenten einen Namen. Er wird als Entwurf erstellt; es läuft nichts, bis Sie ihn veröffentlichen und deployen.',
        ],
      },
      {
        heading: 'Leer',
        body: [
          'Mit einem leeren Agenten beginnen und die Schritte selbst im Designer bauen. Dafür braucht der Mandant die Berechtigung «Eigene Agents erstellen». Plattform-Admins können hier auch eine Plattform-Vorlage anlegen.',
        ],
      },
      {
        heading: 'Beschreiben',
        body: [
          'Beschreiben Sie in eigenen Worten, was der Agent tun soll; die KI entwirft einen vollständigen Agenten, den Sie danach im Designer prüfen und anpassen.',
        ],
      },
    ],
  },

  agentOverview: {
    title: 'Agent-Übersicht',
    intro:
      'Die Versionen dieses Agenten und wo er deployt ist. Jede Änderung ergibt eine neue Version; veröffentlichte Versionen ändern sich nie mehr.',
    sections: [
      {
        heading: 'Versionen',
        items: [
          'Entwurf: in Arbeit, im Designer bearbeitbar. Es gibt höchstens einen Entwurf gleichzeitig.',
          'Veröffentlicht: getestet und eingefroren; kann deployt werden. «Deployt» markiert die Version, die live ist.',
          'Designer / Ansehen öffnet den Designer; Diff vergleicht zwei Versionen; JSON zeigt die Rohdefinition.',
          '«+» startet einen neuen Entwurf als Kopie dieser Version.',
        ],
      },
      {
        heading: 'Schaltflächen',
        items: [
          'Entwurf erstellen: eine neue Version beginnen, um den Agenten zu ändern.',
          'Veröffentlichen: den Entwurf einfrieren. Er darf keine Fehler haben und muss vorher seine Testfälle (Evals) bestehen.',
          'Deployen: eine veröffentlichte Version für diesen Mandanten einschalten.',
          'Entwurf löschen: nur für Agenten, die nie veröffentlicht, nie deployt und nie gelaufen sind.',
        ],
      },
    ],
  },

  templateOverview: {
    title: 'Vorlagen-Übersicht',
    intro: 'Eine Vorlage ist ein fertiger Agent, mit dem Mandanten starten können. Diese Seite zeigt ihre Versionen.',
    sections: [
      {
        heading: 'Versionen',
        items: [
          'Entwurf: in Arbeit. Veröffentlicht: Mandanten können sie nutzen. Zurückgezogen: wird Mandanten nicht mehr angeboten; Agenten, die bereits darauf basieren, arbeiten weiter.',
          'Designer / Ansehen öffnet den Designer; Diff vergleicht zwei Versionen; JSON zeigt die Rohdefinition.',
        ],
      },
      {
        heading: 'Vier-Augen-Prinzip',
        body: [
          'Eine Vorlagenversion braucht einen zweiten Plattform-Admin: Entweder gibt ein anderer Admin sie frei, oder ein anderer Admin veröffentlicht sie. Wer sie geschrieben hat, kann sie nicht allein veröffentlichen.',
        ],
      },
      {
        heading: 'Entwurf löschen',
        body: ['Nur Vorlagen, die nie veröffentlicht wurden, können gelöscht werden, und nur von Plattform-Admins.'],
      },
    ],
  },

  designer: {
    title: 'Designer',
    intro:
      'Hier bauen Sie einen Agenten als Ablaufdiagramm: Jedes Kästchen (Knoten) ist ein Schritt, die Pfeile sagen, was danach kommt. Entwürfe werden automatisch gespeichert.',
    sections: [
      {
        heading: 'Die Bereiche des Bildschirms',
        items: [
          'Links: die Bausteine. Ziehen Sie einen Schritt oder ein jd-enterprise-Tool auf die Fläche (oder doppelklicken Sie darauf).',
          'Mitte: das Ablaufdiagramm. Schritte verbinden Sie, indem Sie von einem Punkt zum anderen ziehen.',
          'Rechts: die Einstellungen des gewählten Elements. Ist nichts gewählt, die Einstellungen des Agenten selbst: Auslöser, wen er kontaktiert, Kanäle, erlaubte Tools, Grenzen.',
          'Unten: Validierung (zu behebende Probleme), Simulator (Agent ausprobieren), KI-Assistent und JSON.',
        ],
      },
      {
        heading: 'Gefahrlos ausprobieren',
        body: [
          'Der Simulator spielt eine Konversation mit einer erfundenen Person durch. Er sendet nie echte Nachrichten und schreibt nie in jd enterprise. Eine gelungene Simulation können Sie als Testfall speichern.',
        ],
      },
      {
        heading: 'KI-Assistent',
        body: [
          'Beschreiben Sie eine Änderung in eigenen Worten («nach 2 Tagen eine Erinnerung senden»). Der Assistent zeigt einen Vorschlag auf der Fläche; geändert wird erst, wenn Sie auf «Übernehmen» klicken.',
        ],
      },
      {
        heading: 'Veröffentlichen',
        body: [
          'Die Schild-Schaltfläche zeigt, was das Veröffentlichen noch blockiert: Fehler, nicht bestandene Testfälle (Evals) oder, bei Vorlagen, der fehlende zweite Admin. «Veröffentlichen» friert den Entwurf als neue Version ein.',
        ],
      },
      {
        heading: 'Tastatur',
        items: [
          'Ctrl+Z rückgängig, Ctrl+Shift+Z wiederholen, Entf löscht die Auswahl, Ctrl+C / Ctrl+V kopieren und einfügen.',
        ],
      },
    ],
  },

  evals: {
    title: 'Evals (Testfälle)',
    intro:
      'Testfälle prüfen, ob sich der Agent richtig verhält, bevor er veröffentlicht wird, z. B. «wer STOP antwortet, wird nicht mehr kontaktiert». Sie laufen mit einer erfundenen Person und einer simulierten KI, sind also schnell und kostenlos.',
    sections: [
      {
        heading: 'Fälle',
        body: [
          'Jeder Fall beschreibt eine Situation und was passieren muss (oder nicht passieren darf): das erwartete Ergebnis, Grenzen für Nachrichten und Kosten, Tools, die nicht verwendet werden dürfen. Erstellen Sie Fälle hier, speichern Sie sie aus dem Simulator oder lassen Sie sie vom KI-Assistenten entwerfen.',
        ],
      },
      {
        heading: 'Ausführen',
        body: [
          '«Evals ausführen» führt alle Fälle mit dieser Version aus. Die Ergebnisse zeigen, welche Fälle bestanden haben und warum andere nicht. Zum Veröffentlichen müssen die Evals für genau diese Version bestanden sein; fehlen sie, führt das Veröffentlichen sie zuerst aus.',
        ],
      },
    ],
  },

  deploy: {
    title: 'Deployment',
    intro: 'Eine veröffentlichte Version dieses Agenten einschalten, ihn pausieren oder zur vorherigen Version zurückkehren.',
    sections: [
      {
        heading: 'Bevor er live geht: Prüfungen',
        items: [
          'Die Version ist veröffentlicht und für diesen Mandanten gültig.',
          'Das jd-enterprise-Konto hat die Berechtigungen, die der Agent braucht.',
          'Die WhatsApp-Vorlagen sind genehmigt und der Kanal funktioniert.',
          'Die Testfälle (Evals) sind bestanden und Kostengrenzen gesetzt.',
          'Grün: in Ordnung. Rot: blockiert das Deployment. Orange: eine Warnung, die nicht blockiert.',
        ],
      },
      {
        heading: 'Optionen',
        body: [
          'Wählen Sie die Version, optional einen Startzeitpunkt und die Filialen, für die er arbeiten soll (keine Auswahl heisst alle Filialen).',
        ],
      },
      {
        heading: 'Pausieren und zurückrollen',
        body: [
          '«Pausieren» stoppt neue Läufe; laufende Läufe gehen weiter, ausser Sie brechen sie mit ab. «Zurückrollen» wechselt zur vorher deployten Version. Nur Admins können deployen, pausieren und zurückrollen.',
        ],
      },
    ],
  },

  runs: {
    title: 'Läufe',
    intro: 'Ein Lauf ist ein Agent, der einen Fall bearbeitet, z. B. einen Kontakt, von Anfang bis Ende.',
    sections: [
      {
        heading: 'Status',
        items: [
          'Ausstehend, Läuft: startet oder arbeitet gerade.',
          'Wartet: wartet auf eine Antwort oder einen Erinnerungszeitpunkt.',
          'In Prüfung: wartet bei den Prüfungen auf eine Person. Eskaliert: an Ihr Team übergeben.',
          'Abgeschlossen: Ziel erreicht. Teilweise: beendet, ohne das Ziel ganz zu erreichen. Fehlgeschlagen: mit einem Fehler beendet. Abgebrochen: von einer Person gestoppt.',
        ],
      },
      {
        heading: 'Was Sie tun können',
        body: [
          'Nach Status, Agent, Arbeitselement oder Datum filtern. «Simulationen einbeziehen» zeigt auch Testläufe aus dem Designer. Ein Klick auf einen Lauf zeigt jeden seiner Schritte. Plattform-Benutzer können aktive Läufe auswählen und abbrechen.',
        ],
      },
    ],
  },

  runTrace: {
    title: 'Lauf-Details',
    intro: 'Jeder Schritt dieses Laufs, der Reihe nach, mit dem, was hineinging und was herauskam.',
    sections: [
      {
        heading: 'Zeitachse',
        body: [
          'Jede Karte ist ein Schritt. Aufgeklappt zeigt sie Ein- und Ausgabe, die KI-Aufrufe (Modell, Tokens, Kosten) und allfällige Fehler. Fehlgeschlagene Schritte sind bereits aufgeklappt. Für Betrachter sind manche Details ausgeblendet.',
        ],
      },
      {
        heading: 'Seitenleiste',
        body: [
          'Status, Ergebnis, Kosten, das Arbeitselement (der Fall, um den es geht) und die Werte, die der Lauf in jd enterprise gespeichert hat.',
        ],
      },
      {
        heading: 'Schaltflächen',
        items: [
          'Im Designer öffnen: die Agenten-Version sehen, die dieser Lauf verwendet hat.',
          'Run erklären: die KI erklärt, was passiert ist, und kann eine Korrektur vorschlagen.',
          'Lauf abbrechen (Admins): einen noch aktiven Lauf stoppen.',
          'Schritt wiederholen (Plattform-Admins): einen fehlgeschlagenen Schritt erneut ausführen.',
        ],
      },
    ],
  },

  metrics: {
    title: 'Kennzahlen',
    intro: 'Wie gut jeder Agent und jede Filiale in einem Zeitraum gearbeitet hat, gezählt werden die Läufe, die in diesem Zeitraum gestartet sind.',
    sections: [
      {
        heading: 'Spalten',
        items: [
          'Abschluss: Anteil der beendeten Läufe, die ihr Ziel erreicht haben.',
          'Eskalationen: Anteil der Läufe, die an eine Person übergeben wurden.',
          'Korrekturen: wie oft eine prüfende Person korrigieren musste, was der Agent gesammelt hat.',
          'Eingriffe: wie oft überhaupt eine Person etwas tun musste (Prüfung oder Eskalation).',
          'Kosten pro abgeschlossenem Lauf (CHF) und mittlere Dauer bis zum Abschluss (Stunden).',
        ],
      },
      {
        heading: 'Export',
        body: ['Die CSV-Schaltflächen laden die Läufe oder die Prüfungen für eigene Auswertungen herunter.'],
      },
    ],
  },

  conversations: {
    title: 'Konversationen',
    intro: 'Die Nachrichten zwischen den Agenten und Personen. Sie können jederzeit eingreifen und selbst schreiben.',
    sections: [
      {
        heading: 'Abzeichen',
        items: [
          '«24-h-Fenster offen»: Auf WhatsApp können Sie frei schreiben. Ist es geschlossen, können nur genehmigte Vorlagen gesendet werden, bis die Person wieder schreibt.',
          '«Kein Opt-in»: die Person hat WhatsApp-Nachrichten nicht zugestimmt.',
          '«Übernommen»: eine Person antwortet anstelle des Agenten.',
          '«Unaufgefordert»: jemand hat ohne offene Konversation geschrieben.',
        ],
      },
      {
        heading: 'Übernehmen und zurückgeben',
        items: [
          'Übernehmen: der Agent schreibt nicht mehr, und Sie antworten selbst im Feld unten.',
          'An Agent zurückgeben: der Agent macht mit der nächsten Nachricht der Person weiter und weiss, was Sie besprochen haben.',
          'Schliessen: beendet die Konversation.',
          'Nur Prüfer und Admins können übernehmen; jeder Schritt steht im Audit-Log.',
        ],
      },
      {
        heading: 'Ruhezeiten',
        body: [
          'Neue Nachrichten, auch Ihre, werden während der Ruhezeiten des Mandanten zurückgehalten und danach gesendet. Direkte Antworten werden nicht zurückgehalten.',
        ],
      },
    ],
  },

  review: {
    title: 'Prüfungen',
    intro:
      'Was die Agenten gesammelt haben (Daten, Dokumente, Rapporte), wartet hier auf eine Person. In jd enterprise wird nichts gespeichert, bevor es jemand freigegeben hat.',
    sections: [
      {
        heading: 'Status',
        items: [
          'Offen: noch niemand bearbeitet es. In Bearbeitung: jemandem zugewiesen.',
          'Entschieden: freigegeben, korrigiert und freigegeben, oder abgelehnt. Abgelaufen: der Lauf endete, bevor jemand entschieden hat.',
          'Das SLA-Abzeichen zeigt die verbleibende Zeit: orange unter 4 Stunden, rot wenn überfällig. Überfällige Einträge erzeugen zusätzlich eine Eskalation.',
        ],
      },
      {
        heading: 'Was Sie tun können',
        body: ['Nach Status, Art oder Agent filtern oder nur überfällige Einträge zeigen. Ein Klick auf einen Eintrag öffnet den Entscheid.'],
      },
    ],
  },

  reviewDecision: {
    title: 'Eintrag prüfen',
    intro: 'Vergleichen Sie, was der Agent gesammelt hat, mit den Belegen, und entscheiden Sie dann.',
    sections: [
      {
        heading: 'Die drei Bereiche',
        items: [
          'Belege: die Dokumente und der Teil der Konversation, aus dem die Daten stammen.',
          'Vorgeschlagene Daten: was gespeichert wird, mit Prüfungen (gültig, Fehler, Hinweise). Hier können Sie Werte korrigieren.',
          'Kontext: der Lauf, der Agent und ein Link zur Konversation.',
        ],
      },
      {
        heading: 'Entscheiden',
        items: [
          'Freigeben (A): die Daten werden in jd enterprise gespeichert, in Ihrem Namen.',
          'Speichern & freigeben (E): Ihre Korrekturen speichern, dann freigeben.',
          'Ablehnen (R): es wird nichts gespeichert; ein Kommentar ist nötig.',
          'Ist jd enterprise nicht erreichbar, wird der Entscheid nicht gespeichert und der Eintrag bleibt offen, damit Sie es erneut versuchen können.',
        ],
      },
      {
        heading: 'Rapporte',
        body: [
          'Regelverstösse (Pausen, Überstunden, Sonntags- oder Nachtarbeit, …) blockieren die Freigabe. Stimmen sie, kreuzen Sie «Regelverstösse akzeptieren» an und geben eine Begründung an.',
        ],
      },
    ],
  },

  escalations: {
    title: 'Eskalationen',
    intro:
      'Fälle, die die Agenten an Ihr Team übergeben haben: jemand möchte mit einer Person sprechen, eine Beschwerde, eine Nachricht, die nicht zugestellt werden konnte, eine überfällige Prüfung, ein Problem mit jd enterprise und so weiter.',
    sections: [
      {
        heading: 'Status',
        items: [
          'Offen: neu. Bestätigt: jemand hat sie übernommen. Erledigt: mit einem Ergebnis abgeschlossen.',
          'Hohe Dringlichkeit steht zuoberst und benachrichtigt zusätzlich die Plattform-Operatoren.',
        ],
      },
      {
        heading: 'Was Sie tun können',
        body: ['Öffnen Sie eine Eskalation, um zu sehen, warum sie entstand, übernehmen Sie sie und entscheiden Sie, wie der Lauf weitergeht.'],
      },
    ],
  },

  escalation: {
    title: 'Eskalation',
    intro: 'Warum der Agent diesen Fall übergeben hat und was als Nächstes passieren soll.',
    sections: [
      {
        heading: 'Schaltflächen',
        items: [
          'Mir zuweisen: Sie übernehmen die Verantwortung dafür.',
          'Übernehmen: Sie antworten der Person selbst in der Konversation.',
          'Konversation öffnen: die Nachrichten lesen.',
        ],
      },
      {
        heading: 'Erledigen',
        items: [
          'Lauf fortsetzen: der Agent macht dort weiter, wo er angehalten hat.',
          'Abschliessen (teilweise): der Lauf endet als teilweise erledigt, mit Ihrer Notiz.',
          'Lauf abbrechen: der Lauf stoppt und die Konversation wird geschlossen.',
        ],
      },
    ],
  },
};
